/**
 * Redux slice for OSA Risk Assessment state.
 *
 * Manages:
 * - OSA risk factors and prediction data
 * - Loading and error states for risk assessment
 * - Active sleep order flag
 *
 * Pattern: Uses createAsyncThunk for data fetching.
 * Components dispatch thunks instead of using useEffect.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getOsaFactors, refreshOsaPrediction } from '../../services/osaFactorsService';
import { fhirClient } from '../../services/fhirClient';
import { getErrorStatusCode } from '../../interceptor/interceptor';
import { setPatientInfo } from '../smart/smartSlice';
import { setShowAssessmentHistory, setHasActiveSleepOrder } from '../ui/uiSlice';
const initialState = {
    patientAlias: null,
    topFactors: [],
    pastHistory: [],
    metadata: null,
    loading: false,
    refreshing: false,
    predictionError: false,
    predictionErrorMessage: null,
    retryAvailable: false,
    noDataAvailable: false,
    error: null
};
/**
 * Async thunk to fetch patient data and OSA risk factors
 */
export const fetchPatientData = createAsyncThunk('osaRisk/fetchPatientData', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ patientId, patientName, skipOsaFactors = false }, { dispatch, rejectWithValue }) {
    var _c;
    try {
        // Fetch patient demographics if name not already set
        if (!patientName) {
            const patient = yield fhirClient.getPatient(patientId, {
                _elements: 'name,identifier,birthDate,gender'
            });
            const extractedName = fhirClient.extractPatientName(patient);
            const gender = (patient === null || patient === void 0 ? void 0 : patient.gender) ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1) : 'Unknown';
            dispatch(setPatientInfo({ name: extractedName, birthDate: (_c = patient === null || patient === void 0 ? void 0 : patient.birthDate) !== null && _c !== void 0 ? _c : 'Unknown', gender }));
        }
        // When AI scoring is not allowed (e.g. patient under 18), do NOT call
        // /osa-factors — no inference exists and the endpoint would 404. Return an
        // empty result so the card renders the "No OSA risk detected" state.
        if (skipOsaFactors) {
            console.debug('Skipping OSA factors fetch (AI scoring not allowed) for patient:', patientId);
            dispatch(setShowAssessmentHistory(false));
            dispatch(setHasActiveSleepOrder(false));
            return { patientAlias: null, factors: [], pastHistory: [], metadata: null, hasActiveSleepOrder: false };
        }
        console.debug('Fetching OSA risk factors from API for patient:', patientId);
        let result;
        try {
            result = yield getOsaFactors(patientId);
        }
        catch (error) {
            const statusCode = getErrorStatusCode(error);
            if (statusCode !== 404 && statusCode !== 500)
                throw error;
            // No vitals or inference data (404), or server error (500). Show the
            // "No data available" fallback state instead of a generic error.
            console.debug(`No OSA data available (${statusCode}) for patient:`, patientId);
            dispatch(setShowAssessmentHistory(false));
            dispatch(setHasActiveSleepOrder(false));
            return {
                patientAlias: null,
                factors: [],
                pastHistory: [],
                metadata: null,
                hasActiveSleepOrder: false,
                noDataAvailable: true
            };
        }
        console.debug('Received OSA factors:', result.factors, 'Metadata:', result.metadata, 'Has active sleep order:', result.hasActiveSleepOrder);
        // Update UI state for assessment history
        dispatch(setShowAssessmentHistory(result.hasActiveSleepOrder));
        dispatch(setHasActiveSleepOrder(result.hasActiveSleepOrder));
        return result;
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch patient data';
        return rejectWithValue(errorMessage);
    }
}), {
    // Guard against duplicate firing while a load is already in flight
    // (e.g. React 18 StrictMode double-effect).
    condition: (_arg, { getState }) => {
        const { osaRisk } = getState();
        if (osaRisk.loading)
            return false;
    }
});
/**
 * Async thunk to refresh (recalculate) OSA prediction.
 * Prevents duplicate runs via condition check.
 */
export const refreshPrediction = createAsyncThunk('osaRisk/refreshPrediction', (patientId_1, _a) => __awaiter(void 0, [patientId_1, _a], void 0, function* (patientId, { dispatch, rejectWithValue }) {
    try {
        console.debug('Refreshing OSA prediction for patient:', patientId);
        const result = yield refreshOsaPrediction(patientId);
        console.debug('Refreshed OSA prediction:', result.factors, 'Metadata:', result.metadata);
        dispatch(setShowAssessmentHistory(result.hasActiveSleepOrder));
        dispatch(setHasActiveSleepOrder(result.hasActiveSleepOrder));
        return result;
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to refresh prediction';
        return rejectWithValue(errorMessage);
    }
}), {
    condition: (_patientId, { getState }) => {
        const { osaRisk } = getState();
        // Prevent duplicate refresh runs
        console.debug('refreshPrediction condition check — refreshing:', osaRisk.refreshing, 'patientId:', _patientId);
        if (osaRisk.refreshing)
            return false;
    }
});
const osaRiskSlice = createSlice({
    name: 'osaRisk',
    initialState,
    reducers: {
        /**
         * Set loading state
         */
        setLoading: (state, action) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },
        /**
         * Set error state
         */
        setError: (state, action) => {
            state.error = action.payload;
            state.loading = false;
        },
        /**
         * Clear error state
         */
        clearError: (state) => {
            state.error = null;
        },
        /**
         * Clear prediction error state
         */
        clearPredictionError: (state) => {
            state.predictionError = false;
            state.predictionErrorMessage = null;
        },
        /**
         * Clear all OSA risk state
         */
        clearOsaRisk: () => {
            return Object.assign({}, initialState);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPatientData.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.predictionError = false;
            state.predictionErrorMessage = null;
            state.retryAvailable = false;
            state.noDataAvailable = false;
        })
            .addCase(fetchPatientData.fulfilled, (state, action) => {
            var _a, _b, _c, _d;
            state.loading = false;
            state.error = null;
            state.patientAlias = action.payload.patientAlias;
            state.topFactors = action.payload.factors;
            state.pastHistory = action.payload.pastHistory;
            state.metadata = action.payload.metadata;
            state.predictionError = (_a = action.payload.predictionError) !== null && _a !== void 0 ? _a : false;
            state.predictionErrorMessage = (_b = action.payload.errorMessage) !== null && _b !== void 0 ? _b : null;
            state.retryAvailable = (_c = action.payload.retryAvailable) !== null && _c !== void 0 ? _c : false;
            state.noDataAvailable = (_d = action.payload.noDataAvailable) !== null && _d !== void 0 ? _d : false;
        })
            .addCase(fetchPatientData.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        })
            .addCase(refreshPrediction.pending, (state) => {
            state.refreshing = true;
            state.error = null;
        })
            .addCase(refreshPrediction.fulfilled, (state, action) => {
            var _a, _b;
            state.refreshing = false;
            state.error = null;
            // In case update prediction fails (soft error with predictionError: true),
            // update with new factors/metadata if provided, or retain previous data.
            if (action.payload.predictionError) {
                state.predictionError = true;
                state.predictionErrorMessage = (_a = action.payload.errorMessage) !== null && _a !== void 0 ? _a : null;
                state.retryAvailable = (_b = action.payload.retryAvailable) !== null && _b !== void 0 ? _b : false;
                if (action.payload.factors && action.payload.factors.length > 0) {
                    state.topFactors = action.payload.factors;
                }
                if (action.payload.pastHistory && action.payload.pastHistory.length > 0) {
                    state.pastHistory = action.payload.pastHistory;
                }
                if (action.payload.metadata) {
                    state.metadata = action.payload.metadata;
                }
                if (action.payload.patientAlias) {
                    state.patientAlias = action.payload.patientAlias;
                }
                return;
            }
            state.patientAlias = action.payload.patientAlias;
            state.topFactors = action.payload.factors;
            state.pastHistory = action.payload.pastHistory;
            state.metadata = action.payload.metadata;
            state.predictionError = false;
            state.predictionErrorMessage = null;
            state.retryAvailable = false;
        })
            .addCase(refreshPrediction.rejected, (state, action) => {
            var _a;
            state.refreshing = false;
            state.error = action.payload;
            state.predictionError = true;
            state.predictionErrorMessage = (_a = action.payload) !== null && _a !== void 0 ? _a : null;
        });
    }
});
export const { setLoading, setError, clearError, clearPredictionError, clearOsaRisk } = osaRiskSlice.actions;
export default osaRiskSlice.reducer;
